import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const values = [
  {
    title: 'Proces & Leverancesikkerhed',
    description: 'Vi gør komplekse opgaver enkle og sikrer, at de bliver leveret som aftalt. Gennem en struktureret proces og stærk koordinering skaber vi fremdrift og ensartet kvalitet – fra første idé til færdig installation.',
  },
  {
    title: 'Kreativitet & Udvikling',
    description: 'Vi arbejder tæt sammen med kunder og samarbejdspartnere om at udvikle løsninger, der kan realiseres. Vi bidrager aktivt i designprocessen med fokus på materialer, konstruktion og muligheder – så idéen holder hele vejen.',
  },
  {
    title: 'Relation & Kultur',
    description: 'Vi bygger samarbejder på tillid, gennemsigtighed og ansvar. Hos os får I ærlig rådgivning, en fast kontakt og en partner, der tager ejerskab for både proces og resultat.',
  },
];

const sustainabilityPoints = [
  'Overholder gældende EU-standarder',
  'Løsninger designet til lang levetid',
  'LED-teknologi med lavt energiforbrug',
  'Lokal produktion med reduceret transport',
  'Fokus på materialevalg og genanvendelse hvor muligt',
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-24">
      <SEO 
        title="Om Os"
        pathname="/om-os"
        description="Lær NeonSign at kende. Siden 1994 har vi leveret skiltning og wayfinding til Danmarks største brands. Vi kombinerer kreativitet med håndværk og leverancesikkerhed."
      />
      {/* Hero */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6 animate-fade-in">
                Om os
              </p>
              <h1 
                data-testid="about-title"
                className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-8 animate-fade-in-up"
              >
                Vi leverer skiltning og wayfinding til projekter med høje krav
              </h1>
              <p className="text-[#666666] text-lg leading-relaxed animate-fade-in-up stagger-2">
                Siden 1994 har Neonsign arbejdet med skiltning på tværs af kæder, hoteller og offentlige projekter. Vi kombinerer æstetik med struktur og tager ansvar for hele leverancen – fra idé og design til produktion og montering. Resultatet er løsninger, der ikke bare ser rigtige ud, men fungerer i praksis og bliver leveret som aftalt.
              </p>
            </div>
            <div className="image-hover animate-fade-in-up stagger-3">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/mlqqmv3b_251206_NEONSIGN_CHP-160.jpg"
                  alt="NeonSign arbejde"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-[#F8F8F8] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
              Vores mission
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1A1A1A] leading-relaxed mb-8">
              At være den partner, der får komplekse projekter til at fungere i praksis
            </h2>
            <p className="text-[#666666] leading-relaxed max-w-2xl mx-auto">
              Vi arbejder for at kombinere design, skiltning og systematisk wayfinding i løsninger, der kan realiseres, implementeres og driftes. For vores kunder handler det ikke kun om udtryk – men om leverance, sammenhæng og et resultat, der holder over tid.
            </p>
          </div>
        </div>
      </section>

      {/* Values / Fundament */}
      <section data-testid="values-section" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
            Vores fundament
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-16">
            Det vi står for
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {values.map((value) => (
              <div key={value.title} className="border-t border-[#E5E5E5] pt-8">
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">
                  {value.title}
                </h3>
                <p className="text-[#666666] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full width image */}
      <section className="relative h-[60vh] image-hover">
        <img
          src="https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/dfl0xumr_251206_NEONSIGN_CHP-238.jpg"
          alt="Politiken"
          className="w-full h-full object-cover"
        />
      </section>

      {/* Bæredygtighed / ESG */}
      <section data-testid="esg-section" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6">
                Bæredygtighed
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-6">
                Ansvarlig produktion – i praksis
              </h2>
              <p className="text-[#666666] leading-relaxed mb-8">
                Vi arbejder med ansvarlig produktion som en integreret del af vores leverance. Det handler ikke kun om materialer, men om holdbarhed, kvalitet og løsninger, der fungerer over tid. Ved at tænke langsigtet i både design og produktion reducerer vi behovet for udskiftning og sikrer løsninger med længere levetid.
              </p>
              <ul className="space-y-3">
                {sustainabilityPoints.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#1A1A1A] rounded-full" />
                    <span className="text-[#666666] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="image-hover">
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_neon-display-1/artifacts/08w4zlmg_251206_NEONSIGN_CHP-197.jpg"
                  alt="Bæredygtig produktion"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6">
            Lad os arbejde sammen
          </h2>
          <p className="text-white/60 mb-10 max-w-lg mx-auto">
            Vi er partneren, der tager ansvar for hele leverancen – fra idé og design til produktion og montering.
          </p>
          <Link
            to="/kontakt"
            data-testid="about-cta"
            className="btn-light inline-flex items-center gap-2"
          >
            Kontakt os
          </Link>
        </div>
      </section>
    </main>
  );
}
